import React, { useCallback, useContext, useMemo } from "react";
import { FlatList } from "react-native";
import { LazyQueryNetworkBoundrary } from "../../components/mobile/QueryNetworkBoundrary";
import { TeamScheduleAdminScreenProps } from "./TeamScheduleAdminStack";
import { graphql, useLazyLoadQuery, useMutation } from "react-relay";
import { AHSeparator, ListItem, useLayoutContext } from "arkh";
import { TeamScheduleAdminTemplateScreenQuery } from "./__generated__/TeamScheduleAdminTemplateScreenQuery.graphql";
import { keyExtractorId } from "../../common/keyExtractors";
import { useModalInsets } from "../../components/mobile/useModalInsets";
import { TeamScheduleAdminTemplateScreenMutation } from "./__generated__/TeamScheduleAdminTemplateScreenMutation.graphql";

import { TeamScheduleAdminPendingRoutineCreatedContext } from "./TeamScheduleAdminPendingRoutineCreatedProvider";

function InnerTeamScheduleAdminTemplateScreenComponent(
  props: {
    fetchKey: string;
  } & TeamScheduleAdminScreenProps<"TeamScheduleAdminTemplates">
) {
  const data = useLazyLoadQuery<TeamScheduleAdminTemplateScreenQuery>(
    graphql`
      query TeamScheduleAdminTemplateScreenQuery($teamId: ID!) {
        currentStore @required(action: THROW) {
          team(id: $teamId) {
            routineTemplates {
              id
              description
              title
            }
          }
        }
      }
    `,
    { teamId: props.route.params.teamId },
    { fetchKey: props.fetchKey, fetchPolicy: "store-and-network" }
  );

  const [createRoutineFromTemplate] =
    useMutation<TeamScheduleAdminTemplateScreenMutation>(graphql`
      mutation TeamScheduleAdminTemplateScreenMutation(
        $templateId: ID!
        $teamId: ID!
      ) {
        createRoutineFromTemplate(templateId: $templateId, teamId: $teamId) {
          id
        }
      }
    `);

  const listData = useMemo(() => {
    return data.currentStore.team?.routineTemplates ?? [];
  }, [data]);

  const { setPendingRoutineCreated } = useContext(
    TeamScheduleAdminPendingRoutineCreatedContext
  );

  const onPress = useCallback(
    (templateId: string) => {
      createRoutineFromTemplate({
        variables: {
          templateId,
          teamId: props.route.params.teamId,
        },
        onCompleted: (res) => {
          if (res.createRoutineFromTemplate?.id) {
            props.navigation.pop();
            setPendingRoutineCreated(res.createRoutineFromTemplate.id);
            props.navigation.navigate("RoutineEdit", {
              id: res.createRoutineFromTemplate.id,
              createFreq: props.route.params.freq,
              createDailyDaysFilter: props.route.params.selectedDaysFilter,
              createDailyStartTimeHour: 12,
              createDailyStartTimeMinute: 0,
              mode: "CREATE",
            });
          }
        },
      });
    },
    [
      createRoutineFromTemplate,
      props.navigation,
      props.route.params.freq,
      props.route.params.selectedDaysFilter,
      props.route.params.teamId,
      setPendingRoutineCreated,
    ]
  );

  const renderItem = useCallback(
    ({ item, index }: { item: (typeof listData)[number]; index: number }) => {
      return (
        <ListItem
          isFirst={index === 0}
          isLast={index === listData.length - 1}
          onPress={() => onPress(item.id)}
          right={<ListItem.NavArrow />}
          border="border-primary"
        >
          <ListItem.Title>{item.title}</ListItem.Title>
          {item.description ? (
            <ListItem.SubTitle>{item.description}</ListItem.SubTitle>
          ) : null}
        </ListItem>
      );
    },
    [listData.length, onPress]
  );

  const layout = useLayoutContext();
  const insets = useModalInsets();
  return (
    <FlatList
      data={listData}
      renderItem={renderItem}
      keyExtractor={keyExtractorId}
      ItemSeparatorComponent={AHSeparator}
      contentContainerStyle={{
        padding: layout.space.medium,
        paddingBottom: insets.bottom + layout.space.medium,
      }}
    />
  );
}

export function TeamScheduleAdminTemplateScreen(
  props: TeamScheduleAdminScreenProps<"TeamScheduleAdminTemplates">
) {
  return (
    <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
      {(fetchKey) => (
        <InnerTeamScheduleAdminTemplateScreenComponent
          {...props}
          fetchKey={fetchKey}
        />
      )}
    </LazyQueryNetworkBoundrary>
  );
}
